import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { motion } from 'framer-motion';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { 
  tokenomicsConfig, 
  generateVestingTimeline, 
  calculateCirculatingSupply,
  formatPercentage 
} from '@/data/tokenomicsData';
import { Download, Play, Pause, RotateCcw } from 'lucide-react';

export const VestingSimulator = () => {
  const svgRef = useRef(null);
  const [currentMonth, setCurrentMonth] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });
  
  const vestingData = generateVestingTimeline();
  
  // Responsive dimensions
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current?.parentElement) {
        const parent = svgRef.current.parentElement;
        setDimensions({ 
          width: Math.min(parent.clientWidth - 40, 900), 
          height: 350 
        });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Auto-play animation
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentMonth(prev => {
        if (prev >= 60) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 150);
    
    return () => clearInterval(interval);
  }, [isPlaying]);
  
  // D3 Chart rendering
  useEffect(() => {
    if (!svgRef.current) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    const { width, height } = dimensions;
    const margin = { top: 20, right: 30, bottom: 50, left: 60 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    
    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);
    
    // Scales
    const xScale = d3.scaleLinear()
      .domain([0, 60])
      .range([0, innerWidth]);
    
    const yScale = d3.scaleLinear()
      .domain([0, 100])
      .range([innerHeight, 0]);
    
    // Grid lines
    g.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(d3.axisBottom(xScale)
        .tickSize(-innerHeight)
        .tickFormat('')
        .ticks(12)
      )
      .selectAll('line')
      .style('stroke', 'hsl(215, 20%, 20%)')
      .style('stroke-opacity', 0.3);
    
    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale)
        .tickSize(-innerWidth)
        .tickFormat('')
        .ticks(5)
      )
      .selectAll('line')
      .style('stroke', 'hsl(215, 20%, 20%)')
      .style('stroke-opacity', 0.3);
    
    // Remove domain lines from grid
    g.selectAll('.grid .domain').remove();
    
    // Axes
    g.append('g')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(d3.axisBottom(xScale)
        .tickValues([0, 6, 12, 18, 24, 30, 36, 42, 48, 54, 60])
        .tickFormat(d => `M${d}`)
      )
      .selectAll('text')
      .style('fill', 'hsl(210, 15%, 60%)')
      .style('font-size', '11px');
    
    g.append('g')
      .call(d3.axisLeft(yScale)
        .tickFormat(d => `${d}%`)
        .ticks(5)
      )
      .selectAll('text')
      .style('fill', 'hsl(210, 15%, 60%)')
      .style('font-size', '11px');
    
    // Style axis lines
    g.selectAll('.domain')
      .style('stroke', 'hsl(215, 20%, 25%)');
    
    g.selectAll('.tick line')
      .style('stroke', 'hsl(215, 20%, 25%)');
    
    // Area gradient for circulating supply
    const areaGradient = svg.append('defs')
      .append('linearGradient')
      .attr('id', 'areaGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');
    
    areaGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', 'hsl(192, 75%, 50%)')
      .attr('stop-opacity', 0.3);
    
    areaGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', 'hsl(192, 75%, 50%)')
      .attr('stop-opacity', 0);
    
    // Line generators
    const lineCirculating = d3.line()
      .x(d => xScale(d.month))
      .y(d => yScale(d.circulating))
      .curve(d3.curveMonotoneX);
    
    const lineInvestors = d3.line()
      .x(d => xScale(d.month))
      .y(d => yScale(d.investors))
      .curve(d3.curveMonotoneX);
    
    const lineTeam = d3.line()
      .x(d => xScale(d.month))
      .y(d => yScale(d.team))
      .curve(d3.curveMonotoneX);
    
    const areaCirculating = d3.area()
      .x(d => xScale(d.month))
      .y0(innerHeight)
      .y1(d => yScale(d.circulating))
      .curve(d3.curveMonotoneX);
    
    // Filter data up to current month
    const filteredData = vestingData.filter(d => d.month <= currentMonth);
    
    // Draw area
    g.append('path')
      .datum(filteredData)
      .attr('fill', 'url(#areaGradient)')
      .attr('d', areaCirculating);
    
    // Draw lines
    g.append('path')
      .datum(filteredData)
      .attr('fill', 'none')
      .attr('stroke', 'hsl(192, 75%, 50%)')
      .attr('stroke-width', 2.5)
      .attr('d', lineCirculating)
      .style('filter', 'drop-shadow(0 0 6px hsl(192, 75%, 50%))');
    
    g.append('path')
      .datum(filteredData)
      .attr('fill', 'none')
      .attr('stroke', 'hsl(35, 80%, 50%)')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '6,3')
      .attr('d', lineInvestors);
    
    g.append('path')
      .datum(filteredData)
      .attr('fill', 'none')
      .attr('stroke', 'hsl(15, 70%, 55%)')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '6,3')
      .attr('d', lineTeam);
    
    // Current point indicator
    if (filteredData.length > 0) {
      const lastPoint = filteredData[filteredData.length - 1];
      
      // Vertical line at current month
      g.append('line')
        .attr('x1', xScale(currentMonth))
        .attr('x2', xScale(currentMonth))
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', 'hsl(192, 75%, 50%)')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,4')
        .attr('opacity', 0.5);
      
      // Point on circulating line
      g.append('circle')
        .attr('cx', xScale(currentMonth))
        .attr('cy', yScale(lastPoint.circulating))
        .attr('r', 6)
        .attr('fill', 'hsl(192, 75%, 50%)')
        .attr('stroke', 'hsl(215, 28%, 7%)')
        .attr('stroke-width', 2)
        .style('filter', 'drop-shadow(0 0 8px hsl(192, 75%, 50%))');
    }
    
    // Interactive overlay
    const focus = g.append('g').style('display', 'none');
    
    focus.append('line')
      .attr('class', 'focus-line')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .style('stroke', 'hsl(192, 75%, 50%)')
      .style('stroke-dasharray', '3,3')
      .style('opacity', 0.5);
    
    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .on('mousemove', function(event) {
        const [mx] = d3.pointer(event);
        const month = Math.round(xScale.invert(mx));
        const clampedMonth = Math.max(0, Math.min(60, month));
        const data = vestingData[clampedMonth];
        
        if (data) {
          setHoveredPoint({
            month: clampedMonth,
            circulating: data.circulating,
            investors: data.investors,
            team: data.team,
            x: mx
          });
          
          focus.style('display', null);
          focus.select('.focus-line')
            .attr('x1', xScale(clampedMonth))
            .attr('x2', xScale(clampedMonth));
        }
      })
      .on('mouseleave', function() {
        setHoveredPoint(null);
        focus.style('display', 'none');
      })
      .on('click', function(event) {
        const [mx] = d3.pointer(event);
        const month = Math.round(xScale.invert(mx));
        setCurrentMonth(Math.max(0, Math.min(60, month)));
      });
    
  }, [dimensions, currentMonth, vestingData]);
  
  // Export to CSV
  const exportCSV = useCallback(() => {
    const headers = ['Month', 'Circulating Supply %', 'Investor Unlock %', 'Team Unlock %'];
    const rows = vestingData.map(d => 
      [d.month, d.circulating.toFixed(2), d.investors.toFixed(2), d.team.toFixed(2)]
    );
    
    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'neuro_vesting_schedule.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [vestingData]);
  
  const currentData = vestingData[currentMonth];
  
  return (
    <div className="space-y-6">
      {/* Current Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Month</p>
          <p className="text-2xl font-bold text-primary">{currentMonth}</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Circulating</p>
          <p className="text-2xl font-bold text-primary">
            {formatPercentage(currentData?.circulating || 0)}
          </p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Investor Unlocked</p>
          <p className="text-2xl font-bold" style={{ color: 'hsl(35, 80%, 50%)' }}>
            {formatPercentage(currentData?.investors || 0)}
          </p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Team Unlocked</p>
          <p className="text-2xl font-bold" style={{ color: 'hsl(15, 70%, 55%)' }}>
            {formatPercentage(currentData?.team || 0)}
          </p>
        </div>
      </div>
      
      {/* Chart */}
      <div className="glass rounded-xl p-4 md:p-6 relative">
        <svg ref={svgRef} className="w-full" />
        
        {/* Hover Tooltip */}
        {hoveredPoint && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute glass rounded-lg p-3 pointer-events-none z-10"
            style={{
              left: hoveredPoint.x + 80,
              top: 40,
              transform: hoveredPoint.x > dimensions.width - 200 ? 'translateX(-100%)' : 'none'
            }}
          >
            <p className="text-xs font-medium text-foreground mb-2">
              Month {hoveredPoint.month}
            </p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">Circulating:</span>
                <span className="font-medium">{formatPercentage(hoveredPoint.circulating)}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(35, 80%, 50%)' }} />
                <span className="text-muted-foreground">Investors:</span>
                <span className="font-medium">{formatPercentage(hoveredPoint.investors)}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(15, 70%, 55%)' }} />
                <span className="text-muted-foreground">Team:</span>
                <span className="font-medium">{formatPercentage(hoveredPoint.team)}</span>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-primary rounded" />
            <span className="text-muted-foreground">Circulating Supply</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 rounded" style={{ 
              background: 'repeating-linear-gradient(90deg, hsl(35, 80%, 50%) 0, hsl(35, 80%, 50%) 4px, transparent 4px, transparent 7px)' 
            }} />
            <span className="text-muted-foreground">Investor Unlock</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 rounded" style={{ 
              background: 'repeating-linear-gradient(90deg, hsl(15, 70%, 55%) 0, hsl(15, 70%, 55%) 4px, transparent 4px, transparent 7px)' 
            }} />
            <span className="text-muted-foreground">Team Unlock</span>
          </div>
        </div>
      </div>
      
      {/* Controls */}
      <div className="flex flex-col md:flex-row items-center gap-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentMonth(0)}
            className="border-border hover:bg-muted"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPlaying(!isPlaying)}
            className="border-border hover:bg-muted"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCSV}
            className="border-border hover:bg-muted"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
        
        <div className="flex-1 w-full md:w-auto">
          <div className="flex items-center gap-4">
            <span className="text-xs text-muted-foreground w-12">Month 0</span>
            <Slider
              value={[currentMonth]}
              onValueChange={([value]) => setCurrentMonth(value)}
              max={60}
              step={1}
              className="flex-1"
            />
            <span className="text-xs text-muted-foreground w-16">Month 60</span>
          </div>
        </div>
      </div>
      
      {/* Vesting Rules */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4">
          <h4 className="font-heading font-semibold text-foreground mb-2 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(35, 80%, 50%)' }} />
            Investor Vesting
          </h4>
          <p className="text-sm text-muted-foreground">
            6 month cliff followed by 12 month linear vesting. 
            Full unlock at month 18.
          </p>
        </div>
        <div className="glass rounded-xl p-4">
          <h4 className="font-heading font-semibold text-foreground mb-2 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(15, 70%, 55%)' }} />
            Team Vesting
          </h4>
          <p className="text-sm text-muted-foreground">
            24 month cliff followed by 36 month linear vesting. 
            Full unlock at month 60.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VestingSimulator;
